<template>
  <div class="report-page">
    <ReportHeaderTab />

    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">Bin Card</h5>
        <p class="page-sub">Track stock movements and running balances per product and store</p>
      </div>

      <div class="filters-wrap">

        <!-- From -->
        <div class="filter-group">
          <div class="filter-label">From</div>
          <div class="date-input-wrap">
            <font-awesome-icon icon="calendar" class="date-icon" />
            <Datepicker v-model="tableData.start_date" :enableTimePicker="false" class="date-picker" />
          </div>
        </div>

        <div class="filter-sep">—</div>

        <!-- To -->
        <div class="filter-group">
          <div class="filter-label">To</div>
          <div class="date-input-wrap">
            <font-awesome-icon icon="calendar" class="date-icon" />
            <Datepicker v-model="tableData.end_date" :enableTimePicker="false" class="date-picker" />
          </div>
        </div>

        <!-- Store -->
        <div class="filter-group">
          <div class="filter-label">Store</div>
          <Multiselect
            v-model="tableData.store"
            label="name"
            :options="getStore"
            placeholder="All Stores"
            :searchable="true"
            :create-option="false"
            :close-on-select="true"
            class="filter-multiselect"
          />
        </div>

        <!-- Product -->
        <div class="filter-group">
          <div class="filter-label">Product</div>
          <Multiselect
            v-model="tableData.product"
            label="name"
            :options="getProduct"
            placeholder="All Products"
            :searchable="true"
            :create-option="false"
            :close-on-select="true"
            class="filter-multiselect"
          />
        </div>

        <!-- Apply -->
        <button class="btn-apply" :disabled="crudLoading" @click="fetchData()">
          <font-awesome-icon :icon="crudLoading ? 'spinner' : 'filter'" :spin="crudLoading" />
          {{ crudLoading ? 'Applying...' : 'Apply' }}
        </button>

        <!-- Export -->
        <div class="dropdown">
          <button class="btn-outline" @click="exportOpen = !exportOpen">
            <font-awesome-icon icon="download" /> Export
          </button>
          <ul class="dropdown-menu" :class="{ show: exportOpen }">
            <li>
              <a class="dropdown-item" href="#" @click.prevent="exportPdf">
                <font-awesome-icon icon="file-pdf" class="text-danger me-2" /> PDF
              </a>
            </li>
            <li>
              <a class="dropdown-item" href="#" @click.prevent="exportCsv">
                <font-awesome-icon icon="file-csv" class="text-success me-2" /> CSV
              </a>
            </li>
          </ul>
        </div>

      </div>
    </div>

    <!-- Summary cards -->
    <div class="summary-grid" v-if="details.length > 0 && !loading">
      <div class="summary-card">
        <div class="summary-icon summary-icon--blue">
          <font-awesome-icon icon="play-circle" />
        </div>
        <div>
          <div class="summary-label">Beginning Balance</div>
          <div class="summary-value">{{ beginning_bal }}</div>
        </div>
      </div>
      <div class="summary-card">
        <div class="summary-icon summary-icon--green">
          <font-awesome-icon icon="arrow-up" />
        </div>
        <div>
          <div class="summary-label">Total In</div>
          <div class="summary-value">{{ totalIn }}</div>
        </div>
      </div>
      <div class="summary-card">
        <div class="summary-icon summary-icon--amber">
          <font-awesome-icon icon="arrow-down" />
        </div>
        <div>
          <div class="summary-label">Total Out</div>
          <div class="summary-value">{{ totalOut }}</div>
        </div>
      </div>
      <div class="summary-card" :class="ending_bal < 0 ? 'summary-card--loss' : 'summary-card--neutral'">
        <div class="summary-icon summary-icon--indigo">
          <font-awesome-icon icon="stop-circle" />
        </div>
        <div>
          <div class="summary-label">Ending Balance</div>
          <div class="summary-value">{{ ending_bal }}</div>
        </div>
      </div>
    </div>

    <!-- Data card -->
    <div class="data-card">

      <!-- Loading -->
      <div v-if="loading" class="state-block">
        <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
        <p class="mt-3 text-muted-cell">Loading bin card data...</p>
      </div>

      <!-- Empty -->
      <div v-else-if="details.length === 0" class="state-block">
        <div class="state-icon">
          <font-awesome-icon icon="clipboard-list" />
        </div>
        <p class="state-title">No bin card records found</p>
        <p class="state-sub">Select a product and store, then adjust the date range and click Apply.</p>
      </div>

      <template v-else>

        <!-- Toolbar -->
        <div class="table-toolbar">
          <span class="table-count">
            Showing <strong>{{ pagination.from }}–{{ pagination.to }}</strong>
            of <strong>{{ pagination.total }}</strong> transactions
          </span>
          <div class="toolbar-right">
            <label class="table-count">Rows:</label>
            <select v-model="tableData.length" @change="tableData.page = 1; fetchData()" class="rows-select">
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="30">30</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option :value="pagination.total">All</option>
            </select>
          </div>
        </div>

        <!-- Table -->
        <div class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Date</th>
                <th>Transaction Type</th>
                <th>Transaction No.</th>
                <th class="text-right">Quantity</th>
                <th class="text-right">Cumulative</th>
              </tr>
            </thead>
            <tbody>

              <!-- Beginning balance row -->
              <tr class="balance-row balance-row--start">
                <td colspan="5" class="balance-label">
                  <font-awesome-icon icon="play-circle" class="me-2 text-blue" />
                  Beginning Balance
                </td>
                <td class="text-right fw-bold">{{ beginning_bal }}</td>
              </tr>

              <!-- Transaction rows -->
              <tr v-for="(bin, index) in details" :key="bin.id">

                <td class="text-muted-cell">{{ pagination.from + index }}</td>

                <td class="text-muted-cell" style="white-space:nowrap;">{{ bin.date }}</td>

                <td>
                  <span class="txn-type-badge" :class="txnTypeClass(bin.type)">
                    <font-awesome-icon :icon="txnTypeIcon(bin.type)" class="me-1" />
                    {{ bin.type }}
                  </span>
                </td>

                <td>
                  <span class="batch-code">{{ bin.transaction_number }}</span>
                </td>

                <td class="text-right fw-bold" :class="isOutflow(bin.type) ? 'outflow-val' : 'inflow-val'">
                  {{ isOutflow(bin.type) ? '-' : '+' }}{{ bin.quantity }}
                </td>

                <td class="text-right fw-500">{{ cumulative[index] }}</td>

              </tr>

              <!-- Ending balance row -->
              <tr class="balance-row balance-row--end">
                <td colspan="5" class="balance-label">
                  <font-awesome-icon icon="stop-circle" class="me-2 text-indigo" />
                  Ending Balance
                </td>
                <td class="text-right fw-bold">{{ ending_bal }}</td>
              </tr>

            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div class="table-footer">
          <pagination
            :pagination="pagination"
            @prev="tableData.page = pagination.currentPage - 1; fetchData()"
            @next="tableData.page = pagination.currentPage + 1; fetchData()"
          />
        </div>

      </template>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { reusableMixin } from '../../../lib/reusableMixin';
import Multiselect from '@vueform/multiselect';
import ReportHeaderTab from './components/reportheadertab.vue';

export default {
  name: 'BinCard',

  components: { ReportHeaderTab, Multiselect },

  mixins: [reusableMixin],

  data() {
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

    return {
      print_content:     [],
      headerArrayColumn: ['S/N', 'Date', 'Transaction', 'Transaction No', 'Quantity', 'Cumulative'],
      footerRowArray:    [],

      details:    [],
      products:   [],
      getProduct: [{ value: 'All', name: 'All Products' }],
      loading:     false,
      crudLoading: false,
      exportOpen:  false,

      tableData: { page: 1,
        store:      'All',
        product:    'All',
        start_date: sixMonthsAgo,
        end_date:   new Date(),
        draw:       0,
        length:     10,
        search:     '',
        column:     0,
        dir:        'desc',
        query:      '',
      },

      pagination: {
        lastPage: '', currentPage: '', total: '',
        lastPageUrl: '', nextPageUrl: '', prevPageUrl: '',
        from: 1, to: '',
      },

      beginning_bal: 0,
      ending_bal:    0,
      cumulative:    [],

      toast: useToast(),
    };
  },

  computed: {
    totalIn() {
      return this.details
        .filter(b => !this.isOutflow(b.type))
        .reduce((s, b) => s + (parseInt(b.quantity) || 0), 0);
    },
    totalOut() {
      return this.details
        .filter(b => this.isOutflow(b.type))
        .reduce((s, b) => s + (parseInt(b.quantity) || 0), 0);
    },
  },

  methods: {
    // ── Helpers ─────────────────────────────────────────────────────────
    isOutflow(type) {
      if (!type) return false;
      const t = type.toLowerCase();
      return t.includes('sale') || t.includes('transfer out') || t.includes('adjustment out') || t.includes('out');
    },

    txnTypeClass(type) {
      return this.isOutflow(type) ? 'txn-badge--out' : 'txn-badge--in';
    },

    txnTypeIcon(type) {
      return this.isOutflow(type) ? 'arrow-up-from-bracket' : 'arrow-down-to-bracket';
    },

    // ── Cumulative ───────────────────────────────────────────────────────
    buildCumulative(details) {
      if (!details.length) return;
      this.beginning_bal = details[0].cost ?? 0;
      this.ending_bal    = details[details.length - 1].cost ?? 0;
      this.cumulative    = details.map((b, i) => (details[i].cost ?? 0) + (parseInt(b.quantity) || 0));
    },

    // ── Export ──────────────────────────────────────────────────────────
    exportPdf() {
      this.exportOpen = false;
      this.drawPdf(
        this.print_content, this.headerArrayColumn,
        'l', 'bin-card-report', 'Bin Card Report', '',
      );
    },

    exportCsv() {
      this.exportOpen = false;
      this.reusableMixinToCSV('Bin-card-report.csv');
    },

    // ── Data ────────────────────────────────────────────────────────────
    async loadProducts() {
      try {
        const { data } = await axios.get('/inventory/product');
        this.getProduct = [{ value: 'All', name: 'All Products' }];
        data.data.forEach(p => {
          this.getProduct.push({ value: p.product_name, name: p.product_name });
        });
      } catch (_) {
        this.toast.error('Could not load products.');
      }
    },

    dataToPrint(details) {
      this.print_content = details.map((bin, idx) => ({
        sn:                 idx + 1,
        date:               bin.date,
        transaction:        bin.type,
        transaction_number: bin.transaction_number,
        quantity:           bin.quantity,
        cumulative:         this.cumulative[idx] ?? '',
      }));
    },

    fetchData(url = '/report/bin-card-report') {
      this.loading     = true;
      this.crudLoading = true;
      this.tableData.draw++;

      axios.get(url, { params: this.tableData })
        .then(({ data }) => {
          this.details = data.data.data;
          this.buildCumulative(this.details);
          this.dataToPrint(this.details);
          this.configPagination(data.data);
        })
        .catch(() => {
          this.toast.error('Failed to load bin card data. Please try again.');
        })
        .finally(() => {
          this.loading     = false;
          this.crudLoading = false;
        });
    },

    configPagination(data) {
      this.pagination.lastPage    = data.last_page;
      this.pagination.currentPage = data.current_page;
      this.pagination.total       = data.total;
      this.pagination.lastPageUrl = data.last_page_url;
      this.pagination.nextPageUrl = data.next_page_url;
      this.pagination.prevPageUrl = data.prev_page_url;
      this.pagination.from        = data.from;
      this.pagination.to          = data.to;
    },
  },

  created() {
    this.fetchData();
    this.allStore();
    this.loadProducts();
  },
};
</script>

<style scoped>
/* ── Page ────────────────────────────────────────────────────────────── */
.report-page { display:flex; flex-direction:column; gap:18px; }

/* ── Header ──────────────────────────────────────────────────────────── */
.page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; }
.page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub     { font-size:13px; color:#7a90b0; margin:0; }

/* ── Filters ─────────────────────────────────────────────────────────── */
.filters-wrap     { display:flex; align-items:flex-end; flex-wrap:wrap; gap:10px; }
.filter-group     { display:flex; flex-direction:column; gap:4px; }
.filter-label     { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; }
.filter-sep       { color:#b0bdd0; font-size:16px; margin-bottom:4px; }
.filter-multiselect { min-width:160px; }
.date-input-wrap  { position:relative; display:flex; align-items:center; }
.date-icon        { position:absolute; left:10px; color:#7a90b0; font-size:12px; pointer-events:none; z-index:1; }
.date-picker      { background:none; border:none; padding-left:28px; }
.btn-apply        { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; white-space:nowrap; }
.btn-apply:hover:not(:disabled) { background:#003b8e; }
.btn-apply:disabled { opacity:.55; cursor:not-allowed; }
.btn-outline      { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; white-space:nowrap; }
.btn-outline:hover { background:#f4f7fb; }
.dropdown         { position:relative; }
.dropdown-menu    { position:absolute; top:calc(100% + 6px); right:0; background:#fff; border:1px solid #e4eaf2; border-radius:10px; box-shadow:0 8px 24px rgba(0,0,0,.1); padding:6px; min-width:160px; z-index:200; display:none; }
.dropdown-menu.show { display:block; }
.dropdown-item    { display:flex; align-items:center; gap:8px; padding:8px 12px; font-size:13px; border-radius:7px; color:#1a2b45; cursor:pointer; text-decoration:none; }
.dropdown-item:hover { background:#f4f7fb; }
.me-1 { margin-right:4px; }
.me-2 { margin-right:8px; }

/* ── Summary cards ───────────────────────────────────────────────────── */
.summary-grid          { display:grid; grid-template-columns:repeat(4,1fr); gap:14px; }
.summary-card          { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:18px 20px; display:flex; align-items:center; gap:16px; }
.summary-card--loss    { border-color:#fecaca; background:#fff5f5; }
.summary-card--neutral { border-color:#c5d6f5; background:#edf4ff; }
.summary-icon          { width:44px; height:44px; border-radius:12px; display:flex; align-items:center; justify-content:center; font-size:18px; flex-shrink:0; }
.summary-icon--blue    { background:rgba(0,74,173,.1); color:#004AAD; }
.summary-icon--green   { background:#f0fdf4; color:#16a34a; }
.summary-icon--amber   { background:#fff7ed; color:#e08c00; }
.summary-icon--indigo  { background:#eef2ff; color:#4f46e5; }
.summary-label         { font-size:12px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; margin-bottom:5px; }
.summary-value         { font-size:20px; font-weight:700; color:#1a2b45; }

/* ── Data card ───────────────────────────────────────────────────────── */
.data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }

/* ── States ──────────────────────────────────────────────────────────── */
.state-block { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
.state-icon  { width:60px; height:60px; border-radius:16px; background:rgba(0,74,173,.08); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
.state-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
.state-sub   { font-size:13px; color:#7a90b0; margin:0; }
.text-muted-cell { color:#4a5e78; }
.mt-3 { margin-top:12px; }

/* ── Table toolbar ───────────────────────────────────────────────────── */
.table-toolbar  { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
.table-count    { font-size:13px; color:#7a90b0; }
.table-count strong { color:#1a2b45; }
.toolbar-right  { display:flex; align-items:center; gap:8px; }
.rows-select    { border:1px solid #dde5f0; border-radius:7px; padding:5px 10px; font-size:13px; color:#1a2b45; background:#f8fafc; outline:none; cursor:pointer; }

/* ── Table ───────────────────────────────────────────────────────────── */
.table-wrap  { overflow-x:auto; }
.data-table  { width:100%; border-collapse:collapse; }
.data-table thead tr { background:#f8fafc; }
.data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 16px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
.data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
.data-table tbody tr:last-child { border-bottom:none; }
.data-table tbody tr:hover:not(.balance-row) { background:#f8fafc; }
.data-table tbody td { padding:12px 16px; font-size:13px; vertical-align:middle; }
.text-right { text-align:right; }
.fw-500     { font-weight:500; }
.fw-bold    { font-weight:700; }

/* ── Balance rows ────────────────────────────────────────────────────── */
.balance-row       { background:#f4f7fb; }
.balance-row--start { border-top:2px solid #e4eaf2; }
.balance-row--end  { border-top:2px solid #e4eaf2; border-bottom:none; }
.balance-label     { font-size:12px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:13px 16px; }
.text-blue         { color:#004AAD; }
.text-indigo       { color:#4f46e5; }

/* ── Transaction type badges ─────────────────────────────────────────── */
.txn-type-badge  { display:inline-flex; align-items:center; border-radius:6px; padding:3px 10px; font-size:12px; font-weight:600; border:1px solid transparent; white-space:nowrap; }
.txn-badge--in   { background:#f0fdf4; color:#16a34a; border-color:#bbf7d0; }
.txn-badge--out  { background:#fff7ed; color:#e08c00; border-color:#fed7aa; }

/* ── Quantity colouring ──────────────────────────────────────────────── */
.inflow-val  { color:#16a34a; }
.outflow-val { color:#e08c00; }

/* ── Batch code ──────────────────────────────────────────────────────── */
.batch-code { font-family:monospace; font-size:12px; font-weight:600; color:#1a2b45; background:#f4f7fb; border:1px solid #e4eaf2; border-radius:6px; padding:2px 8px; }

/* ── Footer ──────────────────────────────────────────────────────────── */
.table-footer { padding:14px 20px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; }

/* ── Responsive ──────────────────────────────────────────────────────── */
@media (max-width:1024px) { .summary-grid { grid-template-columns:repeat(2,1fr); } }
@media (max-width:768px)  {
  .page-header, .filters-wrap { flex-direction:column; align-items:stretch; }
  .filter-sep     { display:none; }
  .filter-multiselect { width:100%; }
  .summary-grid   { grid-template-columns:1fr 1fr; }
}
@media (max-width:480px) { .summary-grid { grid-template-columns:1fr; } }
</style>